import type { ExcalidrawElement } from "@excalidraw/element/types";

import { serializeAsJSON } from "./json";

import type { AppState, BinaryFiles } from "../types";

const DRIVE_SCOPES = "https://www.googleapis.com/auth/drive.file";

// Token client and in-memory access token cache
let tokenClient: any = null;
let accessToken: string | null = null;
let tokenObtainedAtMs: number | null = null;
let gsiScriptPromise: Promise<void> | null = null;

// Type definitions for Google Identity Services
declare global {
  interface Window {
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient: (config: any) => any;
        };
      };
    };
  }
}

const loadGsiScript = async () => {
  if (gsiScriptPromise) {
    return gsiScriptPromise;
  }

  gsiScriptPromise = new Promise<void>((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Google Identity Services"));
    document.head.appendChild(script);
  });

  return gsiScriptPromise;
};

const requestAccessToken = (clientId: string): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    try {
      await loadGsiScript();

      if (!window.google?.accounts?.oauth2) {
        reject(new Error("Google Identity Services not available"));
        return;
      }

      if (!tokenClient) {
        tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: DRIVE_SCOPES,
          callback: (response: any) => {
            if (response.error) {
              reject(new Error(response.error));
              return;
            }
            accessToken = response.access_token;
            tokenObtainedAtMs = Date.now();
            resolve(response.access_token);
          },
        });
      }

      tokenClient.requestAccessToken({ prompt: "" });
    } catch (error: any) {
      reject(error);
    }
  });
};

const getAccessToken = async (clientId: string): Promise<string> => {
  // If we have an in-memory token and it's not too old, use it
  if (accessToken && tokenObtainedAtMs && Date.now() - tokenObtainedAtMs < 55 * 60 * 1000) {
    return accessToken;
  }

  const stored =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("googleDriveAccessToken")
      : null;

  if (stored) {
    accessToken = stored;
    // Fallback: if stored token causes 401 later, we'll refresh on-demand
    return stored;
  }

  const token = await requestAccessToken(clientId);

  if (typeof localStorage !== "undefined") {
    localStorage.setItem("googleDriveAccessToken", token);
  }

  return token;
};

// Generic authenticated fetch with automatic token refresh on 401/403
const fetchWithAuth = async (
  clientId: string,
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> => {
  const token = await getAccessToken(clientId);
  const headers = {
    ...(init.headers || {}),
    Authorization: `Bearer ${token}`,
  } as Record<string, string>;

  let res = await fetch(input, { ...init, headers });
  if (res.status === 401 || res.status === 403) {
    // clear and try once more by requesting a fresh token (may show prompt)
    accessToken = null;
    tokenObtainedAtMs = null;
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem("googleDriveAccessToken");
    }
    const freshToken = await requestAccessToken(clientId);
    const retryHeaders = {
      ...(init.headers || {}),
      Authorization: `Bearer ${freshToken}`,
    } as Record<string, string>;
    res = await fetch(input, { ...init, headers: retryHeaders });
  }
  return res;
};

const ensureFolder = async (
  clientId: string,
  folderName: string,
): Promise<string> => {
  let folderId =
    typeof localStorage !== "undefined"
      ? localStorage.getItem("googleDriveFolderId")
      : null;

  if (folderId) {
    return folderId;
  }

  const searchUrl =
    "https://www.googleapis.com/drive/v3/files" +
    `?q=name='${folderName}' and mimeType='application/vnd.google-apps.folder' and trashed=false`;

  const searchRes = await fetchWithAuth(clientId, searchUrl);

  if (!searchRes.ok) {
    throw new Error("Failed to search Google Drive folders");
  }

  const searchData = await searchRes.json();

  if (Array.isArray(searchData.files) && searchData.files.length > 0) {
    folderId = searchData.files[0].id;
  } else {
    const createRes = await fetchWithAuth(clientId, "https://www.googleapis.com/drive/v3/files", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: folderName,
        mimeType: "application/vnd.google-apps.folder",
      }),
    });

    if (!createRes.ok) {
      throw new Error("Failed to create Google Drive folder");
    }

    const newFolder = await createRes.json();
    folderId = newFolder.id;
  }

  if (folderId && typeof localStorage !== "undefined") {
    localStorage.setItem("googleDriveFolderId", folderId);
  }

  return folderId as string;
};

const findFileInFolder = async (
  clientId: string,
  folderId: string,
  targetName: string,
): Promise<string | null> => {
  const searchUrl =
    "https://www.googleapis.com/drive/v3/files" +
    `?q=name='${targetName}' and '${folderId}' in parents and trashed=false` +
    "&fields=files(id,name)";

  const res = await fetchWithAuth(clientId, searchUrl);

  if (!res.ok) {
    throw new Error("Failed to search Google Drive files");
  }

  const data = await res.json();
  if (Array.isArray(data.files) && data.files.length > 0) {
    return data.files[0].id as string;
  }
  return null;
};

export const saveSceneToGoogleDrive = async (params: {
  elements: readonly ExcalidrawElement[];
  appState: AppState;
  files: BinaryFiles;
  clientId: string;
  folderName?: string;
  fileName: string;
}) => {
  const {
    elements,
    appState,
    files,
    clientId,
    folderName = ".excalidraw",
    fileName,
  } = params;

  const folderId = await ensureFolder(clientId, folderName);

  const serialized = serializeAsJSON(elements, appState, files, "local");

  const existingFileId = await findFileInFolder(
    clientId,
    folderId,
    `${fileName}.excalidraw`,
  );

  const baseMetadata = {
    name: `${fileName}.excalidraw`,
    mimeType: "application/json",
  };

  const metadata = existingFileId
    ? baseMetadata
    : { ...baseMetadata, parents: [folderId] };

  const boundary = `foo_bar_baz_${Date.now()}`;
  const multipartBody =
    `--${boundary}\r\n` +
    `Content-Type: application/json; charset=UTF-8\r\n\r\n` +
    `${JSON.stringify(metadata)}\r\n` +
    `--${boundary}\r\n` +
    `Content-Type: application/json\r\n\r\n` +
    `${serialized}\r\n` +
    `--${boundary}--`;

  const uploadUrl = existingFileId
    ? `https://www.googleapis.com/upload/drive/v3/files/${existingFileId}?uploadType=multipart&fields=id,name,webViewLink&addParents=${folderId}`
    : "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,webViewLink";

  const method = existingFileId ? "PATCH" : "POST";

  const response = await fetchWithAuth(clientId, uploadUrl, {
    method,
    headers: {
      "Content-Type": `multipart/related; boundary=${boundary}`,
    },
    body: multipartBody,
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(
      errorData.error?.message || `Upload failed: ${response.statusText}`,
    );
  }

  await response.json();
};

export const driveFileList = async (
  clientId: string,
  folderName: string = ".excalidraw",
): Promise<{ id: string; name: string }[]> => {
  const folderId = await ensureFolder(clientId, folderName);

  const searchUrl =
    "https://www.googleapis.com/drive/v3/files" +
    `?q='${folderId}' in parents and trashed=false` +
    "&fields=files(id,name)";

  const res = await fetchWithAuth(clientId, searchUrl);
  if (!res.ok) {
    throw new Error("Failed to list Google Drive files");
  }
  const data = await res.json();
  return data.files || [];
};

export const driveFileLoad = async (
  clientId: string,
  fileId: string,
): Promise<any> => {
  const url = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
  const res = await fetchWithAuth(clientId, url);
  if (!res.ok) {
    throw new Error("Failed to download file from Google Drive");
  }
  return await res.json();
};
